// ── EmailActivityPanel — captured Microsoft 365 email for a sales record ──
// Same prop shape as NotesPanel on purpose: drop it in anywhere a NotesPanel
// already sits and it behaves the same way. rollup=true reads by accountid, so
// an Account shows every message captured against it and all of its contacts.
//
// Read-only by design. These are real emails in Exchange; the app mirrors them
// and links out. Nothing here edits or deletes mail.
import { useState, useEffect } from "react";
import { B } from "./theme";
import { Btn, Spinner } from "./ui";
import { useApp } from "./appContext";

const DIR = {
  inbound:  { label: "In",  fg: "#185FA5", bg: "#E6F1FB" },
  outbound: { label: "Out", fg: "#0F6E56", bg: "#E1F5EE" },
};

const ENTITY_COLOR = { Account: B.amber, Opportunity: B.cyan, Contact: B.cyanDark, Lead: "#8a8f98" };

const whenLabel = (d) => d
  ? new Date(d).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  : "—";

// The activity view is one row PER LINK, which is what a single-contact
// timeline wants. An account rollup, though, matches the Account link and a
// Contact link for every external person on the thread — so one email with
// three participants renders three or four times. Collapse to one row per
// message and keep the distinct entity tags for the chips.
const dedupeByMessage = (list) => {
  const byMsg = new Map();
  for (const r of list) {
    const k = r.messageid;
    if (!byMsg.has(k)) byMsg.set(k, { ...r, entities: [] });
    const row = byMsg.get(k);
    if (r.entitytype && r.entitytype !== "Account" && !row.entities.includes(r.entitytype)) {
      row.entities.push(r.entitytype);
    }
    // Prefer a real person match over a domain guess when both exist.
    if (r.matchedon === "email") row.matchedon = "email";
  }
  return [...byMsg.values()];
};

const Chip = ({ fg, bg, children, title }) => (
  <span title={title} style={{ fontSize: 10, fontWeight: 700, color: fg, background: bg, borderRadius: 4, padding: "1px 6px", textTransform: "uppercase", whiteSpace: "nowrap" }}>
    {children}
  </span>
);

export const EmailActivityPanel = ({ entitytype, entityid, accountid, rollup, days, limit }) => {
  const { api, currentUser } = useApp();
  // Graph's webLink resolves only inside the mailbox that owns the message.
  // Clicking one for a colleague's mail opens YOUR mailbox, fails to find it,
  // and reports "moved or deleted" — which reads like data loss rather than
  // what it is. So the link is offered only to the mailbox's owner.
  const myEmail = String((currentUser && (currentUser.email || currentUser.Email)) || "").trim().toLowerCase();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState({});          // messageid -> expanded

  const load = () => {
    setLoading(true); setErr(null);
    const params = rollup ? { accountid } : { entitytype, entityid };
    if (days) params.days = days;
    if (limit) params.limit = limit;
    api.get("getEmailActivity", params)
      .then(d => { setRows(dedupeByMessage(Array.isArray(d) ? d : [])); setLoading(false); })
      .catch(e => {
        // The feature may simply not be switched on yet — say so plainly rather
        // than showing a scary error on a page that is otherwise fine.
        setErr(/Invalid object name|getEmailActivity/i.test(e.message)
          ? "Email capture is not set up yet."
          : e.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if ((rollup && accountid) || (!rollup && entitytype && entityid)) load();
    else { setRows([]); setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitytype, entityid, accountid, rollup, days, limit]);

  const shown = filter === "all" ? rows : rows.filter(r => r.direction === filter);

  const counts = {
    all: rows.length,
    inbound: rows.filter(r => r.direction === "inbound").length,
    outbound: rows.filter(r => r.direction === "outbound").length,
  };

  const FilterBtn = ({ id, label }) => (
    <button onClick={() => setFilter(id)} style={{
      background: filter === id ? B.cyan : "none",
      color: filter === id ? "#fff" : "var(--color-text-secondary)",
      border: "0.5px solid " + (filter === id ? B.cyan : "var(--color-border-secondary)"),
      borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: 12,
      fontWeight: filter === id ? 600 : 400,
    }}>{label} ({counts[id]})</button>
  );

  if (loading) return <Spinner />;
  if (err) return <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 2px" }}>{err}</div>;

  return (
    <div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <FilterBtn id="all" label="All" />
        <FilterBtn id="inbound" label="Received" />
        <FilterBtn id="outbound" label="Sent" />
        <div style={{ marginLeft: "auto" }}><Btn onClick={load}>Refresh</Btn></div>
      </div>

      {shown.length === 0
        ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 2px" }}>
            {rows.length === 0 ? "No captured email yet." : "Nothing matches this filter."}
          </div>
        // Tall enough to read a real conversation without scrolling a box
        // inside a scrolling page. Caps at the viewport so the panel never
        // pushes the rest of the record off screen.
        : <div style={{ maxHeight: "min(72vh, 1000px)", overflowY: "auto" }}>
            {shown.map(m => {
              const d = DIR[m.direction] || DIR.inbound;
              const isOpen = !!open[m.messageid];
              return (
                <div key={m.messageid} style={{ padding: "10px 2px", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 3, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <Chip fg={d.fg} bg={d.bg}>{d.label}</Chip>
                      <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{whenLabel(m.sentdatetime)}</span>
                      {m.hasattachments && (
                        <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                          📎 {m.attachmentcount || 1}
                        </span>
                      )}
                      {/* A domain match means we linked by company, not by a known
                          person — worth flagging so nobody over-trusts it. */}
                      {m.matchedon === "domain" && (
                        <Chip fg="#BA7517" bg="#FAEEDA" title="Matched on email domain, not a known contact">domain</Chip>
                      )}
                      {rollup && (m.entities || []).map(e => (
                        <Chip key={e} fg="#fff" bg={ENTITY_COLOR[e] || B.cyan}>{e}</Chip>
                      ))}
                    </div>
                    {m.weblink && myEmail && String(m.capturedfrom || "").toLowerCase() === myEmail ? (
                      <a href={m.weblink} target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, color: B.cyanDark, textDecoration: "none", whiteSpace: "nowrap" }}>
                        Open in Outlook ↗
                      </a>
                    ) : m.capturedfrom ? (
                      <span style={{ fontSize: 11, color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}
                        title={`Captured from ${m.capturedfrom}. Outlook can only open this from that person's own mailbox.`}>
                        in {String(m.capturedfrom).split("@")[0]}'s mailbox
                      </span>
                    ) : null}
                  </div>

                  <div onClick={() => setOpen(o => ({ ...o, [m.messageid]: !o[m.messageid] }))}
                    style={{ fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                    {m.subject || "(no subject)"}
                  </div>

                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
                    {m.fromname ? m.fromname + " <" + m.fromaddress + ">" : m.fromaddress}
                    {m.toaddresses ? " → " + m.toaddresses : ""}
                    {m.ccaddresses ? "  ·  cc: " + m.ccaddresses : ""}
                  </div>

                  {m.bodypreview && (
                    <div style={{
                      fontSize: 12, color: "var(--color-text-secondary)", marginTop: 4,
                      whiteSpace: "pre-wrap",
                      ...(isOpen ? {} : { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }),
                    }}>{m.bodypreview}</div>
                  )}
                </div>
              );
            })}
          </div>}
    </div>
  );
};
